<script setup lang='ts'>
import type { Schemas } from '#shopware'
import { pascalCase } from 'scule'

// Captured before the first await: afterwards the Nuxt instance context is gone and
// useRequestEvent() can no longer resolve the event for useRouteCache().
const requestEvent = useRequestEvent()

useRouteCache((helper) => {
    helper.setMaxAge(60).setCacheable()
})

const { resolvePath } = useNavigationSearch()
const { fetchPopups } = usePagePopups()
const route = useRoute()
const { locale } = useI18n()
// Strip only a leading /de segment, not "de" occurring anywhere in the slug
const routePath = route.path.replace(new RegExp(`^/${locale.value}(?=/|$)`), '') || '/'
const { getPageComponent } = useFrontendPageRegistry()

const { data: pageData, error } = await useAsyncData(
    `cmsResponse${routePath}`,
    () => Promise.all([resolvePath(routePath), fetchPopups(routePath)]),
)

const seoResult = computed(() => pageData.value?.[0])
const popups = computed(() => pageData.value?.[1] ?? [])

/*
 *
 * Dynamically resolve page components
 * If no SEO url was found, show 404 error page
 */
let query = ''
if (route?.query != null && Object.keys(route?.query).length > 0) {
    query = new URLSearchParams(route?.query as Record<string, string>).toString()
}

const shouldShow404 = computed(() => {
    if (error.value?.statusCode === 404) return true
    if (!seoResult.value?.foreignKey) return true
    return false
})

if (seoResult.value?.redirectCode != null) {
    // SEO redirect: URL has moved (e.g. trailing-slash normalisation). Shopware's
    // redirectTarget is a technical seoPathInfo with no notion of our locale-prefix
    // routing — format it through the current locale like any other internal link, or
    // the redirect drops the active language (and can 404, since the slug the target
    // resolves to only exists under that language).
    const isExternalRedirect = seoResult.value.externalTarget ?? false
    const redirectTarget = isExternalRedirect
        ? seoResult.value.redirectTarget
        : useInternationalization(useLocalePath()).formatLink(seoResult.value.redirectTarget as string)
    await navigateTo(`${redirectTarget}${query !== '' ? '?' + query : '' }`, {
        redirectCode: seoResult.value.redirectCode,
        external: isExternalRedirect,
    })
} else {
    // No SEO redirect: check if the resolved page is valid or show 404 error
    if (error.value && error.value?.statusCode !== 404) {
        showError(error.value as Error)
    }

    if (shouldShow404.value) {
        // Keep the miss out of the route cache: otherwise one unresolvable URL pins its
        // response under that path for the whole TTL.
        useRouteCache((helper) => helper.setUncacheable(), requestEvent)

        throw createError({
            statusCode: 404,
            statusMessage: 'Page Not Found',
            fatal: true
        })
    }
}

const { routeName, foreignKey } = useNavigationContext(
    shouldShow404.value ? ref({}) as Ref<Schemas['SeoUrl']> : seoResult as Ref<Schemas['SeoUrl']>,
)

const pageComponent = computed(() => {
    if (shouldShow404.value) return null
    const componentName = pascalCase(routeName.value as string)
    return getPageComponent(componentName)
})

const currentPageType = computed(() => {
    if (routeName.value === 'frontend.navigation.page') return 'category'
    if (routeName.value === 'frontend.detail.page') return 'detail'
    if (routeName.value === 'frontend.landing.page') return 'cms'
    return undefined
})

// Set canonical URL for SEO
const { setCanonicalUrl } = useCanonicalUrl()
setCanonicalUrl()

useHead({
    link: computed(() =>
        (seoResult.value?.extensions?.hreflang ?? []).map((lang, index) => ({
            key: `hreflang-${index}`,
            rel: 'alternate',
            hreflang: lang.locale,
            href: lang.url,
        }))
    ),
})
</script>

<template>
    <main>
        <component :is='pageComponent' v-if='pageComponent' :navigation-id='foreignKey' />
        <PopupRenderer :popups="popups" :current-page-type="currentPageType" />
    </main>
</template>
